import type { GameState } from './types';
import type { BoardGraph } from '@shared/board/graph';
import type { DistanceCache } from '@shared/board/distance-cache';
import { appendEvent } from './event-log';
import { clearLastMove } from './movement-options';

const MAX_HP = 30;

/**
 * Select the graveyard node furthest from the treasure chest.
 * Spec §3: "Game picks graveyard furthest from chest."
 *
 * @param state - Current game state (used for treasureChestNodeId).
 * @param graph - Board graph (used to find graveyard nodes by type).
 * @param distanceCache - Pre-computed distance cache for findFurthest.
 * @returns The ID of the furthest graveyard node, or null if no graveyards exist.
 */
export function selectGraveyard(
  state: GameState,
  graph: BoardGraph,
  distanceCache: DistanceCache,
): string | null {
  const graveyards = graph.getNodesByType('graveyard');
  if (graveyards.length === 0) return null;
  const graveyardIds = graveyards.map((n) => n.id);
  const furthestReachable = distanceCache.findFurthest(state.treasureChestNodeId, graveyardIds);
  if (furthestReachable !== null) return furthestReachable;

  const chest = graph.getNode(state.treasureChestNodeId);
  if (chest === undefined) return graveyardIds[0] ?? null;

  let furthestByPosition = graveyards[0] ?? null;
  let furthestDistanceSquared = -1;
  for (const graveyard of graveyards) {
    const dx = graveyard.position.x - chest.position.x;
    const dy = graveyard.position.y - chest.position.y;
    const dz = graveyard.position.z - chest.position.z;
    const distanceSquared = dx * dx + dy * dy + dz * dz;
    if (distanceSquared > furthestDistanceSquared) {
      furthestByPosition = graveyard;
      furthestDistanceSquared = distanceSquared;
    }
  }

  return furthestByPosition?.id ?? null;
}

/**
 * Respawn a dead player at the graveyard furthest from the treasure chest.
 *
 * Effects:
 * - Sets player.isDead = false
 * - Restores player.hp to MAX_HP (30)
 * - Moves player.nodeId to the selected graveyard
 * - Sets player.besideGraveyardId to the graveyard nodeId (beside-immunity)
 * - Emits a RespawnEvent
 *
 * @param state - Current game state (mutated in place).
 * @param playerId - ID of the player to respawn.
 * @param graph - Board graph for graveyard lookup.
 * @param distanceCache - Distance cache for furthest graveyard selection.
 * @throws Error if the player is not found or no graveyard nodes exist.
 */
export function respawnPlayer(
  state: GameState,
  playerId: string,
  graph: BoardGraph,
  distanceCache: DistanceCache,
): void {
  const player = state.players.get(playerId);
  if (!player) throw new Error(`Player not found: ${playerId}`);

  const graveyardId = selectGraveyard(state, graph, distanceCache);
  if (!graveyardId) throw new Error('No graveyard nodes found on the board');

  player.isDead = false;
  player.hp = MAX_HP;
  player.nodeId = graveyardId;
  clearLastMove(state, playerId);
  player.besideGraveyardId = graveyardId;

  appendEvent(state, {
    type: 'respawn',
    playerId,
    graveyardNodeId: graveyardId,
  });
}

/**
 * Clear the beside-graveyard immunity flag for a player.
 * Called when the player leaves the graveyard tile (moves away on their next turn).
 *
 * Effects:
 * - Sets player.besideGraveyardId = null
 * - No event emitted
 *
 * @param state - Current game state (mutated in place).
 * @param playerId - ID of the player whose immunity to clear.
 * @throws Error if the player is not found.
 */
export function clearBesideImmunity(state: GameState, playerId: string): void {
  const player = state.players.get(playerId);
  if (!player) throw new Error(`Player not found: ${playerId}`);
  player.besideGraveyardId = null;
}
